import { QueryClient } from '@tanstack/react-query'
import {
  selfRegisterDetailQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import useParamsType, { MatchParams } from 'src/hooks/useParamsType.hook'
import {
  SelfRegisterCouponDetailSpace,
  SelfRegisterCouponImageDeleteSpace,
  SelfRegisterCouponImageUploadSpace,
  SelfRegisterCouponUpdateSpace,
} from 'src/types/api'
import { Params } from 'react-router-dom'

import useSelfRegisterForm from 'src/hooks/react-hook-form/useSelfRegisterForm.hook'
import { FormObjectType, SelfRegisterCouponFormItemsType } from 'src/types/react-hook-form'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import {
  selfRegisterCouponImageDeleteQueryOption,
  selfRegisterCouponImageUploadQueryOption,
  selfRegisterCouponUpdateQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { useEffect, useState } from 'react'
import { DateUtils } from 'src/utils/date'
import SelfRegCouponUpdatePageView from 'src/pages/SelfRegCouponPage/SelfRegCouponUpdatePage/view'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import { useToast } from 'src/provider/ToastContextProvider'
import usePageNav from 'src/hooks/usePageNav.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'

export interface SelfRegUpdatePageProps {
  selfRegisterDetailItems?: SelfRegisterCouponDetailSpace.Response | null
  registerAttribute: Record<keyof SelfRegisterCouponFormItemsType, Partial<FormObjectType>>
  selfRegCouponUpdateHandler: SubmitHandler<SelfRegisterCouponFormItemsType>
  imgStatus: 'default' | 'fail' | 'success'
  imageOnErrorHandler: () => void
  imageOnLoadHandler: () => void
}

export const selfRegisterUpdatePageLoader =
  (queryClient: QueryClient) =>
  async ({ params: { couponIdx } }: { params: Params<keyof MatchParams> }) => {
    return Promise.allSettled([
      await queryClient.ensureQueryData(selfRegisterDetailQueryOption({ Idx: Number(couponIdx!) })),
    ])
  }

const SelfRegCouponUpdatePage = () => {
  const { couponIdx } = useParamsType()
  const { selfRegisterListPageNav } = usePageNav()
  const { openToast } = useToast()
  const { DateFormat } = DateUtils
  const [imgStatus, setImgStatus] = useState<'default' | 'fail' | 'success'>('default')
  const imageOnErrorHandler = () => setImgStatus('fail')
  const imageOnLoadHandler = () => setImgStatus('success')

  const { mutateAsync: selfImgUploadMutate, isPending: isSelfImgUploadPending } = useCustomMutation<
    SelfRegisterCouponImageUploadSpace.Request,
    SelfRegisterCouponImageUploadSpace.Response
  >(selfRegisterCouponImageUploadQueryOption())

  const {
    data: selfRegisterDetailQueryData,
    isLoading: isSelfRegisterDetailLoading,
    isSuccess: isSelfRegisterDetailSuccess,
  } = useCustomQuery<SelfRegisterCouponDetailSpace.Response>(
    selfRegisterDetailQueryOption({ Idx: Number(couponIdx) }),
  )
  const { mutateAsync: selfImgDeleteMutate, isPending: isSelfImgDeletePending } = useCustomMutation<
    SelfRegisterCouponImageDeleteSpace.Request,
    SelfRegisterCouponImageDeleteSpace.Response
  >(selfRegisterCouponImageDeleteQueryOption())

  const { mutateAsync: selfUpdateMutate } = useCustomMutation<
    SelfRegisterCouponUpdateSpace.Request,
    SelfRegisterCouponUpdateSpace.Response
  >(selfRegisterCouponUpdateQueryOption())

  const { methods, registerAttribute } = useSelfRegisterForm({
    selfImgUploadMutate,
    selfImgDeleteMutate,
  })
  const { setValue } = methods

  useEffect(() => {
    if (isSelfRegisterDetailSuccess) {
      const { BrandName, CouponName, Memo, Barcode, CouponImage, ExpireDT, UseFlag } =
        selfRegisterDetailQueryData?.Body!
      setValue('couponImage', CouponImage)
      setValue('validDate', DateFormat(ExpireDT, 'YYYY-MM-DD'))
      setValue('brandName', BrandName)
      setValue('barcode', Barcode)
      setValue('memo', Memo)
      setValue('useFlag', Boolean(UseFlag))
      setValue('couponName', CouponName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelfRegisterDetailSuccess, selfRegisterDetailQueryData])

  const selfRegCouponUpdateHandler: SubmitHandler<SelfRegisterCouponFormItemsType> = (data) => {
    const { couponImage, couponName, brandName, memo, barcode, useFlag, validDate } = data
    const submitData: SelfRegisterCouponUpdateSpace.Request = {
      CouponImageURL: couponImage,
      CouponName: couponName,
      BrandName: brandName,
      Barcode: barcode,
      Idx: Number(couponIdx),
      Memo: memo,
      ExpireDT: validDate,
      UseFlag: Number(useFlag),
    }
    selfUpdateMutate(submitData).then(({ Header, Body }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        openToast({ contents: '내용이 수정되었습니다' })
        selfRegisterListPageNav(true)
      }
    })
  }

  const props: SelfRegUpdatePageProps = {
    registerAttribute,
    imgStatus,
    selfRegCouponUpdateHandler,
    imageOnErrorHandler,
    imageOnLoadHandler,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf
        when={isSelfImgUploadPending || isSelfRegisterDetailLoading || isSelfImgDeletePending}
      >
        <Loading.Default />
      </RenderIf>
      <SelfRegCouponUpdatePageView {...props} />
    </FormProvider>
  )
}
export default SelfRegCouponUpdatePage
